import {
  ClockIcon,
  DramaIcon,
  FingerprintIcon,
  ImagesIcon,
  UsersIcon,
} from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  CastList,
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  MediaGallery,
  MetaDot,
  RawJsonCard,
  ShowHero,
  SystemSection,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { PlayViewType, playViewSchema } from "@/pages/plays/schemas/play.view";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

function formatDuration(minutes?: number | null): string | undefined {
  if (!minutes || minutes <= 0) return undefined;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(" ");
}

export const PlaysShow = () => {
  const { record, raw } = useShowRecord<PlayViewType>(
    playViewSchema,
    (r) => `Play | ${r.title ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={record.media?.photos?.[0]}
              eyebrow={record.originalLanguage}
              title={record.title ?? "Untitled"}
              subtitle={
                record.originalTitle && record.originalTitle !== record.title
                  ? record.originalTitle
                  : undefined
              }
              meta={
                <>
                  {formatDuration(record.durationInMinutes) && (
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="size-3.5" />
                      {formatDuration(record.durationInMinutes)}
                    </span>
                  )}
                  {typeof record.intermission === "number" &&
                    record.intermission > 0 && (
                      <>
                        <MetaDot />
                        <span>{record.intermission}m intermission</span>
                      </>
                    )}
                  {typeof record.interestedIn === "number" && (
                    <>
                      <MetaDot />
                      <span>{record.interestedIn} interested</span>
                    </>
                  )}
                </>
              }
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {/* Same shared badge /movies/show uses — see the note there.
                      Plays was the second hand-rolled copy. */}
                  <EnrichmentStatusBadge status={record.enrichmentStatus} />
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                  {(record.genres ?? []).map((g) => (
                    <Badge key={g.id ?? g.name} variant="secondary">
                      {g.name}
                    </Badge>
                  ))}
                </>
              }
              description={record.overview}
            />

            <DetailSection
              title="Cast & Crew"
              icon={<UsersIcon className="size-4" />}
            >
              <CastList cast={record.cast} />
            </DetailSection>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Details"
                icon={<DramaIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Title" value={record.title} />
                  <DetailField
                    label="Original title"
                    value={record.originalTitle}
                  />
                  <DetailField label="Playwright" value={record.playwright} />
                  <DetailField label="Director" value={record.director} />
                  <DetailField
                    label="Production company"
                    value={record.productionCompany}
                  />
                  <DetailField
                    label="Duration"
                    value={formatDuration(record.durationInMinutes)}
                  />
                  <DetailField label="Genres" full>
                    <Chips items={(record.genres ?? []).map((g) => g.name)} />
                  </DetailField>
                  <DetailField label="Tags" full>
                    <Chips items={record.tags} variant="outline" />
                  </DetailField>
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Identifiers"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied play ID" />
                    </span>
                  </DetailField>
                  <DetailField label="Slug" value={record.slug} mono />
                  <DetailField
                    label="Original language"
                    value={record.originalLanguage}
                  />
                </DetailGrid>
              </DetailSection>
            </div>

            <EnrichmentSummary
              resource="plays"
              id={record.id}
              status={record.enrichmentStatus}
              enrichment={record.enrichment}
              bordered
            />

            <DetailSection
              title="Media"
              icon={<ImagesIcon className="size-4" />}
            >
              <MediaGallery media={record.media} aspect="portrait" />
            </DetailSection>

            {/* Tina — provenance, and the only non-SQL way to re-queue this
                entity's enrichment. Unlike
                artist/venue/geodata/event this entity has no per-entity enrich
                trigger anywhere in IMS, so `POST /enrichments` is not a second
                path to the same thing — it is the only one. */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <ReEnrichButton entityType="play" entityId={record.id} />
              </div>
              <EntityTinaTrace
                entityType="play"
                entityId={record.id}
                metadata={record.metadata}
                enrichment={record.enrichment}
              />
            </div>

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
